/**
 * AccessControl — Wireless > Wi-Fi Access Lists (catalog kind "acl"; label "Access Control" until
 * OP#1858, the same words as the unbuilt Gateway > Gateway Access Control).
 *
 * MAC/IP access lists: a default mode (allow or deny) plus an ordered list of
 * permit/deny rules matched top-down. Canonical org-scoped catalog objects, referenced
 * by SSIDs. CRUD over /api/v2/catalog/acl. Reuses the Meridian list+dialog pattern.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useCatalog, useSaveCatalogObject, useDeleteCatalogObject, type CatalogObject } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";

const INPUT = "rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand";

interface AclRule { action: "permit" | "deny"; match: string }

export default function AccessControl() {
  const { data, isLoading, error } = useCatalog("acl");
  const [editing, setEditing] = useState<CatalogObject | null>(null);
  const [creating, setCreating] = useState(false);
  const rows = data?.items ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader title="Wi-Fi Access Lists" subtitle="MAC / IP access lists — permit or deny clients, referenced from SSIDs" />
        <RoleGate min="org_admin" mode="hide">
          <Button variant="primary" onClick={() => setCreating(true)}>Add list</Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading access lists…</div>}

      {data && rows.length === 0 && (
        <EmptyState
          title="No access lists yet"
          hint="Create an allow-list (default deny, permit known MACs) or a block-list (default allow, deny specific MACs/subnets), then attach it to an SSID."
        />
      )}

      {rows.length > 0 && (
        <ul className="flex flex-col gap-2">
          {rows.map((a) => (
            <li key={a.id} className="md-card flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-ink">{a.name}</span>
                  <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
                    default {String(a.default_mode)}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  {((a.rules as AclRule[]) ?? []).length} rule(s)
                </div>
              </div>
              <RoleGate min="org_admin" mode="hide">
                <Button variant="secondary" onClick={() => setEditing(a)}>Edit</Button>
              </RoleGate>
            </li>
          ))}
        </ul>
      )}

      {creating && <AclDialog onClose={() => setCreating(false)} />}
      {editing && <AclDialog obj={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function AclDialog({ obj, onClose }: { obj?: CatalogObject; onClose: () => void }) {
  const save = useSaveCatalogObject("acl");
  const del = useDeleteCatalogObject("acl");
  const editing = !!obj;

  const [name, setName] = useState(obj?.name ?? "");
  const [defaultMode, setDefaultMode] = useState<string>((obj?.default_mode as string) ?? "deny");
  const [rules, setRules] = useState<AclRule[]>(((obj?.rules as AclRule[]) ?? []).slice());

  const setRule = (i: number, patch: Partial<AclRule>) =>
    setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRule = () => setRules((rs) => [...rs, { action: "permit", match: "" }]);
  const rmRule = (i: number) => setRules((rs) => rs.filter((_, j) => j !== i));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate(
      {
        ...(editing ? { id: obj!.id } : {}),
        name: name.trim(),
        default_mode: defaultMode,
        rules: rules.filter((r) => r.match.trim()).map((r) => ({ action: r.action, match: r.match.trim() })),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[85vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {editing ? `Edit ${obj!.name}` : "Add access list"}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Rules are evaluated top-down; the first match wins, otherwise the default mode applies.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <div className="grid grid-cols-3 gap-2">
              <label className="col-span-2 flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Guest-allowlist" className={INPUT} />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Default</span>
                <select value={defaultMode} onChange={(e) => setDefaultMode(e.target.value)} className={INPUT}>
                  <option value="deny">deny</option>
                  <option value="allow">allow</option>
                </select>
              </label>
            </div>

            <div className="rounded border border-line bg-surface-2 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">Rules</span>
                <Button variant="ghost" type="button" onClick={addRule}>+ Add rule</Button>
              </div>
              {rules.length === 0 && <div className="text-xs text-ink-muted">No rules — only the default mode applies.</div>}
              <div className="flex flex-col gap-2">
                {rules.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select value={r.action} onChange={(e) => setRule(i, { action: e.target.value as AclRule["action"] })}
                      className={`${INPUT} py-1.5`}>
                      <option value="permit">permit</option>
                      <option value="deny">deny</option>
                    </select>
                    <input value={r.match} onChange={(e) => setRule(i, { match: e.target.value })}
                      placeholder="AA:BB:CC:DD:EE:FF or 10.0.0.0/24"
                      className={`${INPUT} flex-1 py-1.5 font-mono text-xs`} />
                    <Button variant="ghost" type="button" onClick={() => rmRule(i)} aria-label="Remove rule">✕</Button>
                  </div>
                ))}
              </div>
            </div>

            {save.isError && <span className="text-xs text-critical">Could not save the access list.</span>}
            <div className="md-dialog-footer flex items-center justify-between gap-2">
              {editing ? (
                <RoleGate min="org_admin" mode="hide">
                  <Button variant="danger" type="button" disabled={del.isPending}
                    onClick={() => del.mutate(obj!.id, { onSuccess: onClose })}>
                    {del.isPending ? "…" : "Delete"}
                  </Button>
                </RoleGate>
              ) : <span />}
              <div className="flex items-center gap-2">
                <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
